import { useEffect, useState } from "react";
import { Bank, Lock, DownloadSimple, SealCheck, Warning, CheckCircle, ArrowsClockwise } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, fmtDate } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { CCNav, PeriodBar, usePeriods, fmtAmt } from "../components/cc/common";
import { CCGuide } from "../components/cc/GuideBanner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";

const STATUS_STYLE = {
  draft: "bg-sky-100 text-sky-800", approved: "bg-emerald-100 text-emerald-800",
  exported: "bg-indigo-100 text-indigo-800", acknowledged: "bg-teal-100 text-teal-800",
  confirmed_paid: "bg-emerald-200 text-emerald-900", superseded: "bg-slate-100 text-slate-500",
};
const EXC_LABEL = { negative_net: "negative net — blocked from export", unmapped_employee: "no Gusto employee ID" };

function MappingCell({ line, isSuper, locked, onSaved }) {
  const [val, setVal] = useState(line.gusto_employee_id || "");
  const [saving, setSaving] = useState(false);
  useEffect(() => setVal(line.gusto_employee_id || ""), [line.id, line.gusto_employee_id]);
  if (!isSuper || locked) return <span className="font-mono text-xs">{line.gusto_employee_id || <span className="text-slate-400">—</span>}</span>;
  const save = async () => {
    setSaving(true);
    try {
      await api.put("/commission/payroll/mapping", { user_id: line.user_id, gusto_employee_id: val });
      toast.success("Mapping saved — re-prepare the draft to apply it");
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };
  return (
    <span className="flex items-center gap-1">
      <Input className="h-7 w-28 text-xs font-mono" placeholder="Gusto ID" value={val}
        onChange={(e) => setVal(e.target.value)} data-testid={`cc-map-input-${line.user_id}`} />
      {val !== (line.gusto_employee_id || "") && (
        <Button size="sm" variant="outline" className="h-7 px-2 text-xs" disabled={saving} onClick={save}
          data-testid={`cc-map-save-${line.user_id}`}>Save</Button>
      )}
    </span>
  );
}

export default function CommissionPayrollPage() {
  const { user } = useAuth();
  const isSuper = user?.role === "super_admin";
  const { periods, selected, setSelected, reload } = usePeriods();
  const [run, setRun] = useState(null);
  const [lines, setLines] = useState([]);
  const [history, setHistory] = useState([]);
  const [busy, setBusy] = useState(false);
  const [approveOpen, setApproveOpen] = useState(false);
  const [approveNote, setApproveNote] = useState("");
  const [paidOpen, setPaidOpen] = useState(false);
  const [paid, setPaid] = useState({ reference: "", note: "" });

  const load = async (pk = selected) => {
    if (!pk) return;
    const { data } = await api.get(`/commission/payroll/runs?period_key=${pk}`);
    setHistory(data);
    const latest = data.find((r) => r.status !== "superseded") || null;
    setRun(latest);
    if (latest) {
      const d = await api.get(`/commission/payroll/runs/${latest.id}`);
      setLines(d.data.lines);
    } else setLines([]);
  };
  useEffect(() => { load(); }, [selected]);

  const act = (fn, okMsg) => async () => {
    setBusy(true);
    try {
      await fn();
      if (okMsg) toast.success(okMsg);
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Action failed"); }
    finally { setBusy(false); }
  };

  const prepare = act(() => api.post("/commission/payroll/prepare", { period_key: selected }), "Draft payroll run prepared");
  const approve = act(async () => {
    await api.post(`/commission/payroll/runs/${run.id}/approve`, { note: approveNote });
    setApproveOpen(false); setApproveNote("");
  }, "Payroll APPROVED & period LOCKED");
  const genExport = act(() => api.post(`/commission/payroll/runs/${run.id}/export`), "Gusto CSV generated (immutable)");
  const acknowledge = act(() => api.post(`/commission/payroll/runs/${run.id}/acknowledge`, { note: "" }), "Marked acknowledged in Gusto");
  const confirmPaid = act(async () => {
    await api.post(`/commission/payroll/runs/${run.id}/confirm-paid`, paid);
    setPaidOpen(false); setPaid({ reference: "", note: "" });
  }, "External payment confirmed");

  const download = async () => {
    try {
      const res = await api.get(`/commission/payroll/exports/${run.export.export_id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = run.export.filename; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error("Download failed"); }
  };

  const locked = run && run.status !== "draft";
  const exceptions = lines.filter((l) => l.exception);
  const t = run?.totals || {};

  return (
    <div data-testid="commission-payroll-page">
      <div className="flex items-start justify-between flex-wrap gap-3 mb-2">
        <div>
          <h1 className="text-2xl font-heading font-semibold flex items-center gap-2">
            <Bank size={26} weight="duotone" className="text-indigo-600" /> Payroll runs
          </h1>
          <p className="text-sm text-slate-500 mt-1">Super-admin approval freezes the period's ledger into an immutable run. A generated or accepted Gusto file is <strong>not</strong> proof of payment.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <PeriodBar periods={periods} selected={selected} setSelected={setSelected}
            onCreated={async (k) => { await reload(); setSelected(k); }} />
          {isSuper && (!run || run.status === "draft") && (
            <Button size="sm" disabled={busy || !selected} onClick={prepare} data-testid="cc-payroll-prepare">
              <ArrowsClockwise size={15} className="mr-1" /> {run ? "Re-prepare draft" : "Prepare draft run"}
            </Button>
          )}
        </div>
      </div>
      <CCNav />
      <CCGuide periodKey={selected} />

      {!run ? (
        <div className="rounded-lg border border-dashed border-slate-300 p-10 text-center text-slate-500">
          No payroll run for {selected || "this period"} yet{isSuper ? " — Prepare a draft from the posted ledger." : "."}
        </div>
      ) : (
        <>
          <div className="flex items-center gap-2 mb-3 text-xs text-slate-500 flex-wrap" data-testid="cc-payroll-status">
            <Badge className={STATUS_STYLE[run.status]}>payroll v{run.version} · {run.status.replace("_", " ")}</Badge>
            <span>prepared by {run.created_by_name} {fmtDate(run.created_at)}</span>
            {run.approved_at && <span className="flex items-center gap-1"><Lock size={12} /> approved by {run.approved_by_name} {fmtDate(run.approved_at)}</span>}
            <span className="font-mono">manifest {String(run.manifest_hash).slice(0, 10)}…</span>
          </div>

          {run.warnings?.length > 0 && run.status === "draft" && (
            <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 mb-4 text-sm text-amber-800" data-testid="cc-payroll-warnings">
              <p className="font-medium flex items-center gap-1.5 mb-1"><Warning size={15} /> Approval is blocked until resolved:</p>
              <ul className="list-disc ml-5 space-y-0.5">{run.warnings.map((w, i) => <li key={i}>{w}</li>)}</ul>
            </div>
          )}

          <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-5">
            {[["Net payable", t.net, "cc-pay-kpi-net"], ["Export total", t.export_total, "cc-pay-kpi-export"],
              ["Excluded (exceptions)", t.excluded_total, "cc-pay-kpi-excluded"],
              ["Base + bonuses", String((Number(t.base || 0) + Number(t.bonus || 0)).toFixed(2)), "cc-pay-kpi-base"],
              ["Chargebacks + adj.", String((Number(t.chargeback || 0) + Number(t.bonus_reversal || 0) + Number(t.adjustment || 0)).toFixed(2)), "cc-pay-kpi-cb"]].map(([label, v, tid]) => (
              <div key={tid} className="rounded-lg border border-slate-200 bg-white p-4" data-testid={tid}>
                <p className="text-xs uppercase tracking-wide text-slate-500">{label}</p>
                <p className="text-xl font-semibold mt-1">{fmtAmt(v)}</p>
              </div>
            ))}
          </div>

          <div className="rounded-lg border border-slate-200 bg-white overflow-hidden mb-5">
            <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between flex-wrap gap-2">
              <h2 className="font-medium text-sm">Producer payout lines ({t.lines || 0} · {t.eligible || 0} export-eligible · {t.exceptions || 0} exception{Number(t.exceptions) === 1 ? "" : "s"})</h2>
              {isSuper && run.status === "draft" && (
                <Button size="sm" disabled={busy || (run.warnings || []).length > 0} onClick={() => setApproveOpen(true)}
                  title={(run.warnings || []).length ? "Resolve warnings first" : ""} data-testid="cc-payroll-approve-open">
                  <Lock size={15} className="mr-1" /> Approve & lock
                </Button>
              )}
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="cc-payroll-lines">
                <thead><tr className="text-left text-xs text-slate-500 border-b border-slate-100">
                  <th className="px-4 py-2">Member</th><th className="px-4 py-2">Gusto ID</th>
                  <th className="px-4 py-2 text-right">Base</th><th className="px-4 py-2 text-right">Bonuses</th>
                  <th className="px-4 py-2 text-right">Chargebacks</th><th className="px-4 py-2 text-right">Adjustments</th>
                  <th className="px-4 py-2 text-right">Net</th><th className="px-4 py-2 text-right">Prev. period</th>
                  <th className="px-4 py-2">Export</th>
                </tr></thead>
                <tbody>
                  {lines.map((l) => (
                    <tr key={l.id} className="border-b border-slate-50" data-testid={`cc-payroll-line-${l.user_id}`}>
                      <td className="px-4 py-2.5 font-medium">{l.user_name}</td>
                      <td className="px-4 py-2.5"><MappingCell line={l} isSuper={isSuper} locked={locked} onSaved={load} /></td>
                      <td className="px-4 py-2.5 text-right">{fmtAmt(l.base)}</td>
                      <td className="px-4 py-2.5 text-right">{fmtAmt(l.bonus)}</td>
                      <td className="px-4 py-2.5 text-right">{fmtAmt(String((Number(l.chargeback) + Number(l.bonus_reversal)).toFixed(2)))}</td>
                      <td className="px-4 py-2.5 text-right">{fmtAmt(l.adjustment)}</td>
                      <td className="px-4 py-2.5 text-right font-semibold">{fmtAmt(l.net)}</td>
                      <td className="px-4 py-2.5 text-right text-slate-500">{l.prev_net != null ? fmtAmt(l.prev_net) : "—"}</td>
                      <td className="px-4 py-2.5">
                        {l.export_eligible ? <Badge className="bg-emerald-100 text-emerald-800">eligible</Badge>
                          : l.exception ? <Badge className="bg-red-100 text-red-700">{EXC_LABEL[l.exception] || l.exception}</Badge>
                          : <Badge className="bg-slate-100 text-slate-500">not exported</Badge>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {exceptions.length > 0 && (
              <p className="px-4 py-2.5 text-xs text-amber-700 bg-amber-50 border-t border-amber-100">
                Exception lines are preserved in the run and the ledger — they are never zeroed or deducted from unrelated wages. Negative balances carry forward; handle them outside the CSV import per your payroll/legal rules.
              </p>
            )}
          </div>

          <div className="rounded-lg border border-slate-200 bg-white p-4 mb-5" data-testid="cc-payroll-export-card">
            <h2 className="font-medium text-sm mb-2 flex items-center gap-1.5"><DownloadSimple size={16} className="text-indigo-600" /> Gusto CSV export</h2>
            {run.status === "draft" ? (
              <p className="text-sm text-slate-500">Available after approval. Columns: First Name, Last Name, Gusto Employee ID, Commission — verify the headers against the template downloaded from your Gusto account (Run Payroll → Import payroll data) before first use.</p>
            ) : !run.export ? (
              isSuper ? (
                <Button size="sm" disabled={busy} onClick={genExport} data-testid="cc-payroll-export-btn">
                  <DownloadSimple size={15} className="mr-1" /> Generate Gusto CSV
                </Button>
              ) : <p className="text-sm text-slate-500">Awaiting export generation by the super admin.</p>
            ) : (
              <div className="space-y-2 text-sm">
                <p className="flex items-center gap-2 flex-wrap">
                  <span className="font-mono text-xs">{run.export.filename}</span>
                  <span className="text-xs text-slate-400">sha256 {String(run.export.sha256).slice(0, 12)}… · {run.export.line_count} line(s) · total {fmtAmt(run.export.total)}</span>
                </p>
                <div className="flex items-center gap-2 flex-wrap">
                  {isSuper && <Button size="sm" variant="outline" onClick={download} data-testid="cc-payroll-download"><DownloadSimple size={14} className="mr-1" /> Download CSV</Button>}
                  {isSuper && run.status === "exported" && (
                    <Button size="sm" variant="outline" disabled={busy} onClick={acknowledge} data-testid="cc-payroll-ack"><CheckCircle size={14} className="mr-1" /> Mark acknowledged in Gusto</Button>
                  )}
                  {isSuper && ["exported", "acknowledged"].includes(run.status) && (
                    <Button size="sm" disabled={busy} onClick={() => setPaidOpen(true)} data-testid="cc-payroll-paid-open"><SealCheck size={14} className="mr-1" /> Confirm externally paid</Button>
                  )}
                </div>
                {run.export.excluded?.length > 0 && (
                  <p className="text-xs text-amber-700">{run.export.excluded.length} line(s) excluded from the file: {run.export.excluded.map((x) => `${x.user_name} (${fmtExc(x.exception)} ${x.net})`).join("; ")}</p>
                )}
                {run.export_ack && <p className="text-xs text-teal-700">Acknowledged by {run.export_ack.by_name} {fmtDate(run.export_ack.at)}</p>}
                {run.paid_confirmation && <p className="text-xs text-emerald-700">Paid confirmed by {run.paid_confirmation.by_name} {fmtDate(run.paid_confirmation.at)} — ref {run.paid_confirmation.reference}</p>}
              </div>
            )}
          </div>

          {history.length > 1 && (
            <p className="text-xs text-slate-400">Run history: {history.map((r) => `v${r.version} ${r.status}`).join(" · ")}</p>
          )}
        </>
      )}

      <Dialog open={approveOpen} onOpenChange={setApproveOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Approve & lock payroll — {selected}</DialogTitle></DialogHeader>
          <div className="text-xs text-slate-600 space-y-1.5 bg-slate-50 rounded p-3">
            <p>• The run's input manifest (ledger + mappings) is frozen — any change since this draft rejects the approval.</p>
            <p>• The period is <strong>locked</strong>: no further ledger posts or adjustments; corrections go into a later open period.</p>
            <p>• Approved runs are immutable. Net payable: <strong>{fmtAmt(t.net)}</strong> across {t.lines} member(s).</p>
          </div>
          <Textarea placeholder="Approval note / evidence (required, min 5 chars)" value={approveNote}
            onChange={(e) => setApproveNote(e.target.value)} data-testid="cc-payroll-approve-note" />
          <Button disabled={busy || approveNote.trim().length < 5} onClick={approve} data-testid="cc-payroll-approve-confirm">
            <Lock size={15} className="mr-1" /> Approve & lock (audited)
          </Button>
        </DialogContent>
      </Dialog>

      <Dialog open={paidOpen} onOpenChange={setPaidOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Confirm external payment</DialogTitle></DialogHeader>
          <p className="text-xs text-slate-500">Recorded as a separate event with an external reference — the export file itself is never treated as payment.</p>
          <Input placeholder="External reference (Gusto payroll ID, check #…)" value={paid.reference}
            onChange={(e) => setPaid({ ...paid, reference: e.target.value })} data-testid="cc-payroll-paid-reference" />
          <Textarea placeholder="Note (optional)" value={paid.note}
            onChange={(e) => setPaid({ ...paid, note: e.target.value })} data-testid="cc-payroll-paid-note" />
          <Button disabled={busy || paid.reference.trim().length < 3} onClick={confirmPaid} data-testid="cc-payroll-paid-confirm">Confirm paid</Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function fmtExc(e) {
  return e === "negative_net" ? "negative net" : e === "unmapped_employee" ? "unmapped" : e;
}
